<template>
  <AppLoadingContainer :predicate="isLoading">
    <div class="card-body">
      <!-- The collapsable filter options -->
      <AppFormCardCollapsable :display-footer="true" @submit.prevent="onFilterSubmit()" class="mb-3">
          <template v-slot:header>Filter</template>
          <template v-slot:body>
              <div class="form-row justify-content-center">
                  <div class="col-auto form-group">
                      <label for="facilitiesKeyCopyStatusId">Status</label>
                      <select class="form-control" v-model="filterOptions.facilitiesKeyCopyStatusId"
                              @change="clearFilter()">
                          <option :value="null">All</option>
                          <option :key="status.id" :value="status.id" v-for="status in facilitiesKeyCopyStatusList">{{status.name}}</option>
                      </select>
                  </div>
                  <div v-if="filterOptions.facilitiesKeyCopyStatusId === null || filterOptions.facilitiesKeyCopyStatusId.toUpperCase() === facilitiesKeyCopyStatuses.Issued" class="col-md-5 form-group">
                      <label>Issued to Name</label>
                      <input
                              class="form-control"
                              placeholder="Name"
                              v-model="filterOptions.issuedToName"
                      />
                  </div>
                  <div v-if="filterOptions.facilitiesKeyCopyStatusId === null || filterOptions.facilitiesKeyCopyStatusId.toUpperCase() === facilitiesKeyCopyStatuses.Stored" class="col-md-5 form-group">
                      <label>Hook Number</label>
                      <input
                              class="form-control"
                              placeholder="Hook Number"
                              v-model="filterOptions.hookNumber"
                      />
                  </div>
              </div>
          </template>
          <template v-slot:footer>
              <button class="btn btn-primary" type="submit">Apply</button>
          </template>
      </AppFormCardCollapsable>
      <div class="mb-2 text-right">
          <router-link :to="{name:'AddKeyCopies',params:{facilitiesKeyId:facilitiesKeyId,facilitiesKeyTypeName:facilitiesKeyTypeName,facilitiesKeyNumber:facilitiesKeyNumber}}"
                       class="btn btn-sm btn-primary d-print-none"
                       data-toggle="tooltip"
                       title="Add key copy">
              <i class="fa fa-plus"></i>Add Copies
          </router-link>
      </div>
      <BTable :busy="isLoading"
              :fields="tableFields"
              :items="facilitiesKeyCopies"
              :responsive="true"
              primary-key="id"
              show-empty
              striped
              table-class="table-align-middle">

          <template v-slot:cell(id)="row">
            <div class="row">
              <div class="col-4 col-lg-3">
                <a @click="toggleHistories(row)" class="ml-3">
                  <i v-if="!row.item._showDetails" class="fa fa-caret-down fa-lg" />
                  <i v-if="row.item._showDetails" class="fa fa-caret-up fa-lg" />
                </a>
              </div>
              <div class="btn-group col-auto">
                  <router-link
                          :to="{ name:'EditKeyCopy', params:{ id: row.item.id, facilitiesKeyId: facilitiesKeyId, facilitiesKeyNumber: facilitiesKeyNumber, facilitiesKeyTypeName: facilitiesKeyTypeName, facilitiesKeyCopyNumber: row.item.copyNumber } }"
                          class="btn btn-sm btn-warning d-print-none"
                          data-toggle="tooltip"
                          title="Edit key copy">
                      <i class="fa fa-pencil-alt"></i>Edit
                  </router-link>
                  <button @click="onDeleteClick(row.item)" class="btn btn-danger btn-sm d-print-none"
                          type="button"><i class="fa fa-trash"></i>Delete
                  </button>
              </div>
            </div>
          </template>

          <template v-slot:row-details="row">
              <BTable :busy="row.item.isLoadingHistory"
                      :fields="innerTableFields"
                      :items="row.item.facilitiesKeyCopyHistories"
                      :responsive="true"
                      primary-key="id"
                      show-empty
                      table-class="table-align-middle">

                
                  <template v-slot:cell(actionDate)="innerRow">
                      <span>{{ innerRow.item.actionDate | moment('MM/DD/YYYY') }}</span>
                  </template>

                  <!-- Footer Pagination -->
                  <template v-slot:custom-foot>
                      <b-tr>
                          <b-td :colspan="innerTableFields.length">
                              <AppTablePagination
                                      :page.sync="row.item.historyFilterOptions.page"
                                      :pageSize.sync="row.item.historyFilterOptions.pageSize"
                                      :totalCount.sync="row.item.totalFacilitiesKeyCopyHistories"
                                      v-on:update:page="loadHistoryData(row)"
                                      v-on:update:pageSize="loadHistoryData(row)"
                              ></AppTablePagination>
                          </b-td>
                      </b-tr>
                  </template>
              </BTable>
          </template>

          <!-- Footer Pagination -->
          <template v-slot:custom-foot>
              <b-tr>
                  <b-td :colspan="tableFields.length">
                      <AppTablePagination
                              :page.sync="filterOptions.page"
                              :pageSize.sync="filterOptions.pageSize"
                              :totalCount.sync="totalFacilitiesKeyCopies"
                              v-on:update:page="loadData()"
                              v-on:update:pageSize="loadData()"
                      ></AppTablePagination>
                  </b-td>
              </b-tr>
          </template>
      </BTable>
    </div>

    <AppModalConfirm :visible="deletingRow" @close="deletingRow=null" @confirm="onDeleteConfirmed(deletingRow)">
        <template v-slot:header>Confirm</template>
        <p v-if="deletingRow">Are you sure you want to delete key copy {{ deletingRow.copyNumber }}?</p>
    </AppModalConfirm>
  </AppLoadingContainer>
</template>

<script>
    import {ToastType} from "@/services/models/shared/ToastType";
    import AppTablePagination from "../../components/shared/AppTablePagination";
    import FacilitiesKeysService from "@/services/FacilitiesKeysService";
    import AppLoadingContainer from "@/components/shared/AppLoadingContainer";
    import AppModalConfirm from "@/components/shared/AppModalConfirm";
    import AppFormCardCollapsable from "@/components/shared/AppFormCardCollapsable";
    import { FacilitiesKeyCopyStatus } from "@/services/models/FacilitiesKey";

    export default {
        components: {
            AppTablePagination,
            AppLoadingContainer,
            AppModalConfirm,
            AppFormCardCollapsable
        },
        props: {
            facilitiesKeyId: {
                type: String,
                required: true
            },
            facilitiesKeyNumber: {
              type: String,
              required: true
            },
            facilitiesKeyTypeName: {
              type: String,
              required: true
            }
        },
        data() {
            return {
                facilitiesKeyCopies: [],
                totalFacilitiesKeyCopies: 0,
                deletingRow: null,
                filterOptions: {
                    page: 1,
                    pageSize: 10,
                    returnAll: false,
                    facilitiesKeyId: null,
                    facilitiesKeyCopyStatusId: null,
                    buildingId: null,
                    hookNumber: null,
                    issuedToName: null
                },
                isLoading: false,
                tableFields: [
                    {
                        key: 'id',
                        label: '',
                        thStyle: 'width:300px'
                    }, 
                    {
                        key: 'copyNumber',
                        label: 'Copy Number'
                    },
                    {
                        key: 'facilitiesKeyCopyStatusName',
                        label: 'Status',
                    },
                    {
                        key: 'hookNumber',
                        label: 'Hook Number',
                    }
                ],
                innerTableFields: [
                    {
                        key: 'previousFacilitiesKeyCopyStatusName',
                        label: 'Previous Status'
                    },
                    {
                        key: 'newFacilitiesKeyCopyStatusName',
                        label: 'New Status'
                    },
                    {
                        key: 'previousHookNumber',
                        label: 'Previous Hook Number',
                    },
                    {
                        key: 'newHookNumber',
                        label: 'New Hook Number',
                    },
                    {
                        key: 'actionByPersonName',
                        label: 'Action By',
                    },
                    {
                        key: 'actionForPersonName',
                        label: 'Issued To/Returned By',
                    },
                    {
                        key: 'actionDate',
                        label: 'Logged Date',
                    }
                ],
                facilitiesKeyCopyStatuses: FacilitiesKeyCopyStatus,
            }
        },
        computed: {
          facilitiesKeyCopyStatusList () {
              return (Object.keys(FacilitiesKeyCopyStatus)).map(val => {
                return {
                    id: FacilitiesKeyCopyStatus[val],
                    name: val.toString()
                }
              })
          }
        },
        methods: {
            loadHistoryData(row) {
              const vm = this;
              row.item.isLoadingHistory = true;
              FacilitiesKeysService.getFacilitiesKeyCopyHistoriesForKeyCopy(row.item.id, row.item.historyFilterOptions)
              .then(response => {
                row.item.facilitiesKeyCopyHistories = response.data;
                row.item.totalFacilitiesKeyCopyHistories = response.totalCount;
              })
              .catch(() => {
                vm.redirectToError()
              })
              .finally(() => {
                row.item.isLoadingHistory = false;
              })
            },

            loadData() {
                const vm = this;
                vm.isLoading = true;

                FacilitiesKeysService.getFacilitiesKeyCopiesForKey(this.facilitiesKeyId, vm.filterOptions)
                  .then(response => {
                    vm.facilitiesKeyCopies = response.data.map(obj => ({ ...obj, _showDetails: false, facilitiesKeyCopyHistories: [], totalFacilitiesKeyCopyHistories: 0, historyFilterOptions: {
                      page: 1,
                      pageSize: 10,
                      returnAll: false,
                      facilitiesKeyId: null,
                      facilitiesKeyCopyStatusId: null,
                      buildingId: null,
                      hookNumber: null,
                      issuedToName: null
                    }, isLoadingHistory: false }));
                    vm.totalFacilitiesKeyCopies = response.totalCount;
                  })
                  .catch(() => {
                    vm.redirectToError()
                  })
                  .finally(() => {
                    vm.isLoading = false;
                  })
            },

            /**
             * @description Resets the page to the first page and reloads the data
             */
            onFilterSubmit() {
                this.filterOptions.page = 1;
                this.loadData();
            },

            clearFilter () {
              this.filterOptions.hookNumber = null;
              this.filterOptions.issuedToName = null;
            },

            toggleHistories (row) {
              if (!row.item._showDetails) {
                this.loadHistoryData(row);
                row.item._showDetails = true;
              } else {
                row.item.facilitiesKeyCopyHistories = [];
                row.item.totalFacilitiesKeyCopyHistories = 0;
                row.item.historyFilterOptions = {
                    page: 1,
                    pageSize: 10,
                    returnAll: false,
                    facilitiesKeyId: null,
                    facilitiesKeyCopyStatusId: null,
                    buildingId: null,
                    hookNumber: null,
                    issuedToName: null
                };
                row.item._showDetails = false;
              }
            },

            /**
             * @description Opens up a modal to confirm deletion of the key copy
             */
            onDeleteClick(keyCopy) {
                this.deletingRow = keyCopy;
            },

            /**
             * @description When the user clicks 'Confirm' - the selected key copy will be deleted and the modal will be closed.
             */
            onDeleteConfirmed(deletingRow) {
                const vm = this;

                FacilitiesKeysService.deleteFacilitiesKeyCopy(deletingRow.id)
                    .then(() => {
                        vm.alert(ToastType.Success, `The key copy was successfully deleted`);

                        // Reload the data on the page
                        vm.loadData();
                    }).catch(error => {
                      vm.alert(ToastType.Danger, error.response.data)
                    }).finally(() => {
                      vm.deletingRow = null;
                    })
            }
        },
        created() {
            this.loadData();
        }
    }
</script>
